/**
 * ===============================================================================
 * EHR WEB FORM ACTIONS RESTRICT BY PERMISSION - USAGE GUIDE
 * ===============================================================================
 *
 * @description
 * This directive controls button visibility based on user permissions, roles,
 * departments, and job titles.
 *
 * ------------------------------------------------------------------------------
 * @directive
 *  ehr-web-form-actions-restrict-by-permission
 *  → Add this directive to buttons that should be shown or hidden based on permissions.
 *
 * ------------------------------------------------------------------------------
 * @attribute
 *  emrwindowrefno = "windowid"
 *  → Must be added to the top-level <form> or <div> tag.
 *  → 'windowid' represents the routing number (unique window reference).
 *
 * ------------------------------------------------------------------------------
 * @service
 *  WebFormActionsRestrictByPermissionsService
 *  → Inject this service into your controller.
 *
 * ------------------------------------------------------------------------------
 * @function
 *  WebFormActionsRestrictByPermissionsService.GetGlobalValueSetDataBasedOnUsersRolesDeptJobTitle(windowid);
 *  → Loads permissions based on roles, departments, and job titles.
 *
 *  Optional parameters:
 *      WebFormActionsRestrictByPermissionsService.GetGlobalValueSetDataBasedOnUsersRolesDeptJobTitle(windowid, uniqueid, data, dynamicFieldsExists, scope);
 *      - uniqueid : optional unique reference ID
 *      - data     : optional payload if required
 *      - dynamicFieldsExists     : dynamic fields like ag grid context menus exists then pass it as true
 *      - data     : if dynamic fields exists then pass the windows scope as 5th parameter mandatorily
 * ------------------------------------------------------------------------------
 * @setup
 *  Go to:
 *      Settings >>
 *      Move Global Multi Domain Value Sets and Their Values to My Practice Database
 *      (Look Up Table / Value Set Option(s))
 *
 *  1. VALUE SET:
 *      - Click the Add icon.
 *      - Add your window under:
 *            "EHR Web Forms Security Policies / Permissions"
 *      - Set:
 *            • Value Set Name = Window Name
 *            • Coded Value    = Window ID (mandatory)
 *
 *  2. VALUE SET VALUES:
 *      - Click on the created Value Set.
 *      - Click Add to define buttons.
 *      - Set:
 *            • "Enter Value Name"  = Button Display Name
 *            • "Web Form Object / Div ID / Easy Form Template ID"
 *              = Button’s name attribute value (mandatory)
 *
 * ------------------------------------------------------------------------------
 * @behavior
 *  After setup, buttons will be automatically hidden if:
 *      - They are moved to the right, and
 *      - They match users, roles, departments, or job titles restrictions.
 *
 * ===============================================================================
/**
 * ===============================================================================
 * UPDATE: SUPPORT FOR DYNAMIC FIELDS (AG GRID CONTEXT MENUS, NON-DOM ELEMENTS)
 * ===============================================================================
 *
 * @description
 * Added support to evaluate visibility rules for fields that do not exist in the
 * HTML DOM at page load — such as AG Grid context menu items or any dynamically
 * generated UI elements.
 *
 * These items do not use AngularJS directives, and therefore cannot be detected
 * using the regular DOM-based permission verification. The updated functions
 * allow permission processing for these dynamic fields by using the window scope.
 *
 * ------------------------------------------------------------------------------
 * @function
 *  WebFormActionsRestrictByPermissionsService
 *      .GetGlobalValueSetDataBasedOnUsersRolesDeptJobTitle_NoDom(windowid, uniqueid, data, scope);
 *
 *  → Use this function **only when the window contains dynamic fields ONLY**
 *    (e.g., AG Grid context menus) and **no directive-based elements** that rely
 *    on the `ehr-web-form-actions-restrict-by-permission` directive.
 *
 *  @mandatory parameters:
 *      - windowid : Routing/window reference number
 *      - scope    : The current window’s AngularJS scope (required for dynamic fields)
 *
 *  @optional parameters:
 *      - uniqueid : A unique identifier if needed (otherwise pass undefined)
 *      - data     : Optional payload if required by the window (otherwise undefined)
 *
 * ------------------------------------------------------------------------------
 * @function
 *  WebFormActionsRestrictByPermissionsService
 *      .GetGlobalValueSetDataBasedOnUsersRolesDeptJobTitle(windowid, uniqueid, data, dynamicFieldsExists, scope);
 *
 *  → Use this function when the window contains **both**:
 *        • directive-based form controls (DOM elements) AND
 *        • dynamic fields such as AG Grid menu items.
 *
 *  @parameters:
 *      - windowid            : Routing/window reference number (mandatory)
 *      - uniqueid            : Optional unique reference (pass undefined if not used)
 *      - data                : Optional payload (pass undefined if not used)
 *      - dynamicFieldsExists : Set to true if dynamic fields exist in the same window
 *      - scope               : Mandatory ONLY when dynamic fields exist
 *
 * ------------------------------------------------------------------------------
 * @dynamic fields handling (AG Grid context menus)
 *
 *  When preparing AG Grid context menus or any other dynamic field array,
 *  call the below function **BEFORE** sending your menu data to AG Grid:
 *
 *      WebFormActionsRestrictByPermissionsService
 *          .getCustomizedGlobalValueSetDataBasedFields_NoDom(windowid, menus);
 *
 *  @mandatory:
 *      - windowid : Your routing/window number (example: 6577)
 *      - menus    : The array of dynamic menu objects
 *
 *  @menu structure requirement:
 *      Each menu object **must** contain an `_name` property for identification:
 *
 *      const menus = [
 *          {
 *              name: "Open Patient Chart",
 *              _name: "mnuBedSensusFormat2OpenPatientChart", // mandatory
 *              action: BedCensusMaximumViewOpenPatientChart,
 *          },
 *      ];
 *
 *  → The `_name` value must match the "Web Form Object / Div ID / Easy Form Template ID"
 *    configured in Value Set Values for permissions to work correctly.
 *
 * ------------------------------------------------------------------------------
 * @notes
 *  • Always pass `undefined` for optional parameters when not used.
 *  • Use `_NoDom` version **only when no DOM-based permissions are present**.
 *  • Use the regular function when DOM and dynamic fields coexist.
 *  • Dynamic field permission evaluation requires the `_name` property to be present.
 * ===============================================================================
 */


angular.module('EMR.Admin').value("EHRWindowPermissions", {
    permissionsList: {},
    customizedPermissionsList: {},
    permissionsApiCache: {}
 }).directive("ehrWebFormActionsRestrictByPermission", ['EHRWindowPermissions', function (EHRWindowPermissions) {
        return {
            restrict: "A",
            priority: 1002,
            link: {
                pre: function () { },
                post: function ($scope, $element) {
                    try {
                        // Finding nearest parent having emrwindowrefno
                        var parentWithRef = $element.closest('[emrwindowrefno]');
                        if (!parentWithRef || !parentWithRef.attr('emrwindowrefno')) {
                            return; // no parent → do nothing
                        }

                        var windowId = parseInt(parentWithRef.attr('emrwindowrefno'));
                        if (!EHRWindowPermissions.permissionsList[windowId])
                            EHRWindowPermissions.permissionsList[windowId] = [];

                        var name = $element.attr('name');


                        EHRWindowPermissions.permissionsList[windowId].push({
                            name,
                            $element
                        });
                    } catch (e) {
                        console.error('ehrWebFormActionsRestrictByPermission error:', e);
                    }
                }
            }
        };
 }]).service("WebFormActionsRestrictByPermissionsService", ['CommonPostService', '$q', 'EHRWindowPermissions', function (CommonPostService, $q, EHRWindowPermissions) {

     this.GetGlobalValueSetDataBasedOnUsersRolesDeptJobTitle = function (routingNumber, uniqueId, postData = {}, dynamicFieldsExists, scope) {
         if (!routingNumber) return $q.resolve(null);
         postData.Category_Filter_CodedValue = routingNumber;
         postData.Category_Filter_16Digits_UniqueID = uniqueId;
         postData.JobTitleIDs = _.join(_.compact([EMRPracticeModel.LoggedUserPrimaryJobTitleId, EMRPracticeModel.LoggedUserSecondaryJobTitleId]));
         postData.RoleIDs = EMRPracticeModel.LoggedUserRolesIDs;
         postData.DepartmentIDs = EMRPracticeModel.LoggedUserDepartmentIDs;
         postData.practicemodel = EMRPracticeModel;
         return getPermissionsData(routingNumber, uniqueId, postData).then(function (response) {
             if (isError(response)) return;
             let fieldsToBeHidden = response && response.GlobalValueSetRolesUsersDeptModelList || [];
             if (dynamicFieldsExists && _.some(fieldsToBeHidden)) {
                 EHRWindowPermissions.customizedPermissionsList[routingNumber] = fieldsToBeHidden;
                 removePermissionsCacheOnWindowDestroy(routingNumber, scope);
             }
             else delete EHRWindowPermissions.customizedPermissionsList[routingNumber];
             let domElements = EHRWindowPermissions.permissionsList[routingNumber];
             _.forEach(fieldsToBeHidden, function (eachField) {
                 //same button/ icon can be used in multiple places so modified code to handle multiple fields with same permission
                 let domFields = _.filter(domElements, { name: eachField.Appointment_Status });
                 _.forEach(domFields, function (domField) {
                     domField.$element.remove();
                 });
             });
             delete EHRWindowPermissions.permissionsList[routingNumber];
         });
     };
     this.GetGlobalValueSetDataBasedOnUsersRolesDeptJobTitle_NoDom = function (routingNumber, uniqueId, postData = {}, scope) {
         if (!routingNumber) return $q.resolve(null);
         postData.Category_Filter_CodedValue = routingNumber;
         postData.Category_Filter_16Digits_UniqueID = uniqueId;
         postData.JobTitleIDs = _.join(_.compact([EMRPracticeModel.LoggedUserPrimaryJobTitleId, EMRPracticeModel.LoggedUserSecondaryJobTitleId]));
         postData.RoleIDs = EMRPracticeModel.LoggedUserRolesIDs;
         postData.DepartmentIDs = EMRPracticeModel.LoggedUserDepartmentIDs;
         postData.practicemodel = EMRPracticeModel;
         return getPermissionsData(routingNumber, uniqueId, postData).then(function (response) {
             if (isError(response)) return [];
             let customizedFields = response && response.GlobalValueSetRolesUsersDeptModelList || [];
             if (_.some(customizedFields)) {
                 EHRWindowPermissions.customizedPermissionsList[routingNumber] = customizedFields;
                 removePermissionsCacheOnWindowDestroy(routingNumber, scope);
             }
             else delete EHRWindowPermissions.customizedPermissionsList[routingNumber];
             return customizedFields;
         });
     };
     this.getCustomizedGlobalValueSetDataBasedFields_NoDom = function (routingNumber, fieldsList) {
         let fieldsToBeHidden = EHRWindowPermissions.customizedPermissionsList[routingNumber];
         if (!routingNumber || !fieldsToBeHidden) return fieldsList;
         _.forEach(fieldsToBeHidden, function (eachField) {
             _.remove(fieldsList, { _name: eachField.Appointment_Status })
         });
         return fieldsList;
     };
     function removePermissionsCacheOnWindowDestroy(routingNumber, scope) {
         if (!routingNumber || !scope) return;
         scope.$on('$destroy', function () {
             delete EHRWindowPermissions.customizedPermissionsList[routingNumber];
         });
     };
     function getPermissionsCacheKey(routingNumber, uniqueId) {

         // rare case - we don't cache
         if (routingNumber && uniqueId) {
             return null;
         }

         if (routingNumber) {
             return `R_${routingNumber}`;
         }

         return null;
     };
     function getPermissionsData(routingNumber, uniqueId, postData) {

         let cacheKey = getPermissionsCacheKey(routingNumber, uniqueId);

         if (cacheKey && EHRWindowPermissions.permissionsApiCache[cacheKey]) {
             return EHRWindowPermissions.permissionsApiCache[cacheKey];
         }

         let requestPromise = CommonPostService.httpPostData(
             'POST',
             CommonPostService.EHR_GlobalValueSet() +
             "GetGlobalValueSetDataBasedOnUsersRolesDeptJobTitle",
             postData
         ).then(function (response) {

             if (isError(response)) {

                 if (cacheKey) {
                     delete EHRWindowPermissions.permissionsApiCache[cacheKey];
                 }

                 return response;
             }

             return response;
         }, function (error) {

             if (cacheKey) {
                 delete EHRWindowPermissions.permissionsApiCache[cacheKey];
             }

             return $q.reject(error);
         });

         if (cacheKey) {
             EHRWindowPermissions.permissionsApiCache[cacheKey] = requestPromise;
         }

         return requestPromise;
     };

 }]);
